import MuiTextField, {
  type TextFieldProps as MuiTextFieldProps,
} from '@mui/material/TextField';
import { InputAdornment, Typography } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import classNames from 'classnames';
import omit from 'lodash/omit';
import React from 'react';

import type { FieldProps } from '../types';

import {
  getNumberSeparators,
  normalizeNumberInput,
  formatNumberInput,
  parseNumberInput,
} from '../../../utils/numberInput';
import { useNumberDraft } from '../../../hooks/useNumberDraft';
import { HelperMessage } from '../../HelperMessage';
import { ReadOnlyField } from '../../ReadOnlyField';
import { ErrorMessage } from '../../ErrorMessage';
import StepButtons from './StepButtons';

export type NumberFieldProps = {
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Virtual keyboard hint. `type="number"` alone makes iOS open the punctuation
   * plane of the regular keyboard rather than a numeric keypad; MUI needs this
   * to travel through `inputProps` to land on the native input.
   */
  inputMode?: React.InputHTMLAttributes<HTMLInputElement>['inputMode'];
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  withStepButtons?: boolean;
  value?: number | string;
  defaultValue?: number;
  placeholder?: string;
  maxDecimals?: number;
  step?: number;
  min?: number;
  max?: number;
} & Pick<
  MuiTextFieldProps,
  | 'classes'
  | 'disabled'
  | 'label'
  | 'name'
  | 'onBlur'
  | 'onKeyDown'
  | 'required'
  | 'size'
> &
  FieldProps;

const NumberField = React.forwardRef(
  (
    {
      className,
      classes,
      'data-testid': testId,
      defaultValue,
      endAdornment,
      errors,
      fullWidth,
      helperText,
      inputMode = 'decimal',
      loading,
      max,
      maxDecimals,
      min,
      name,
      onBlur,
      onChange,
      readOnly,
      required = false,
      startAdornment,
      step = 1,
      value,
      withStepButtons = false,
      ...props
    }: NumberFieldProps,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);

    // The input is type="text" on purpose: type="number" only accepts "." as a
    // decimal separator, so on a device whose region uses "," the keypad offers
    // a key the field then silently drops — 1,5 became 15. The hook keeps the
    // typed text as-is; the numeric value is parsed out of it.
    const { draft, setDraft, locale } = useNumberDraft(value, defaultValue);

    if (loading) {
      return (
        <Skeleton
          className={classNames(className, { 'w-full': fullWidth })}
          variant="rounded"
          height={58}
        />
      );
    }

    if (readOnly) {
      return (
        <ReadOnlyField
          value={formatNumberInput(value ?? defaultValue, locale)}
          startAdornment={startAdornment}
          endAdornment={endAdornment}
          label={props?.label ?? ''}
          className={className}
          fullWidth={fullWidth}
          errors={errors}
        />
      );
    }

    const roundValue = (num: number) => {
      if (maxDecimals === undefined) return num;
      const factor = 10 ** maxDecimals;
      return Math.round(num * factor) / factor;
    };
    const handleManualStep = (direction: 'down' | 'up') => {
      const currentValue = Number(value ?? defaultValue ?? 0);
      let newValue =
        direction === 'up' ? currentValue + step : currentValue - step;

      if (max !== undefined) newValue = Math.min(newValue, max);
      if (min !== undefined) newValue = Math.max(newValue, min);

      newValue = roundValue(newValue);
      if (onChange) {
        const event = {
          target: { name, value: String(newValue) },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(event);
      }
    };

    // consumers read e.target.value and run it through Number(), so what leaves
    // this component is always canonical ("." separator), never the typed text;
    // the real event is spread underneath when there is one (change), so fields
    // like `type` and `currentTarget` survive for consumers that look at them
    const emit = (
      canonical: string,
      base?: React.ChangeEvent<HTMLInputElement>,
    ) => {
      if (onChange) {
        onChange({
          ...base,
          target: { ...base?.target, name, value: canonical },
        } as React.ChangeEvent<HTMLInputElement>);
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { decimal } = getNumberSeparators(locale);
      let next = normalizeNumberInput(e.target.value, locale);

      if (maxDecimals !== undefined && next.includes(decimal)) {
        const [intPart, decPart] = next.split(decimal);
        if (decPart.length > maxDecimals) {
          next = `${intPart}${decimal}${decPart.slice(0, maxDecimals)}`;
        }
      }

      setDraft(next);
      const parsed = parseNumberInput(next, locale);
      emit(parsed === null ? '' : String(parsed), e);
    };

    // min/max were enforced by the browser while this was type="number"; that
    // check is gone with type="text", so apply it here. Clamping happens on
    // blur, not per keystroke — clamping mid-typing fights the user (typing "5"
    // toward "50" under min=10 would snap to 10 on the first digit).
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);

      const parsed = parseNumberInput(draft, locale);
      if (parsed === null) {
        if (draft !== '') {
          setDraft('');
          emit('');
        }
      } else {
        let corrected = roundValue(parsed);
        if (min !== undefined) corrected = Math.max(corrected, min);
        if (max !== undefined) corrected = Math.min(corrected, max);

        const formatted = formatNumberInput(corrected, locale);
        if (formatted !== draft) {
          setDraft(formatted);
          emit(String(corrected));
        }
      }

      onBlur?.(e);
    };

    return (
      <div className={classNames(className, { 'w-full': fullWidth })}>
        <MuiTextField
          {...omit(props, ['loading', 'errors', 'helperText'])}
          InputProps={{
            endAdornment: (
              <InputAdornment sx={{ gap: 0.5 }} position="end">
                <Typography color="textPrimary" variant="body1" marginRight={1}>
                  {endAdornment}
                </Typography>
                {isFocused && withStepButtons && (
                  <StepButtons
                    handleManualStep={handleManualStep}
                    disabled={props.disabled}
                    value={value}
                    max={max}
                    min={min}
                  />
                )}
              </InputAdornment>
            ),
            startAdornment: startAdornment && (
              <InputAdornment position="start">{startAdornment}</InputAdornment>
            ),
          }}
          inputProps={{ 'data-testid': testId, inputMode }}
          data-testid={`number-field--${testId}`}
          onFocus={(e) => {
            setIsFocused(true);
            e.target.select();
          }}
          error={Boolean(errors)}
          onChange={handleChange}
          fullWidth={fullWidth}
          onBlur={handleBlur}
          // the text as typed; the numeric value travels through onChange
          value={draft}
          required={required}
          variant="outlined"
          classes={classes}
          inputRef={ref}
          type="text"
          name={name}
        />
        {helperText && (
          <HelperMessage className="mt-1 ml-2">{helperText}</HelperMessage>
        )}
        {errors && <ErrorMessage className="mt-1" errors={errors} />}
      </div>
    );
  },
);

NumberField.displayName = 'NumberField';
export default NumberField;
